import type { Control, FieldErrors } from "react-hook-form";

import { Controller } from "react-hook-form";

import { Input } from "@/components/ui/input/input";

import type { LocationGeocodeFormValues } from "../../../hooks/useLocationAddressGeocode";

import {
	getLatitudeError,
	getLongitudeError,
} from "../../../utils/shared/coordinates";

interface LocationEditorFieldsProps {
	control: Control<LocationGeocodeFormValues>;
	errors: FieldErrors<LocationGeocodeFormValues>;
	mapboxAccessToken?: string;
	/** Wrap address Controllers (dashboard: AddressAutofill). */
	wrapAddressFields: (children: React.ReactNode) => React.ReactNode;
}

/**
 * Location name / timezone / address / lat-lng fields matching dashboard
 * location-details Controllers (name + autoComplete for Mapbox autofill).
 */
export const LocationEditorFields = ({
	control,
	errors,
	mapboxAccessToken,
	wrapAddressFields,
}: LocationEditorFieldsProps) => {
	const addressFields = (
		<div className="space-y-3 border-t border-secondary pt-3">
			<p className="text-sm font-medium text-primary">Address</p>
			<Controller
				control={control}
				name="street1"
				render={({ field }) => (
					<Input
						autoComplete="address-line1"
						errorMessage={errors.street1?.message}
						hint={
							mapboxAccessToken
								? "Start typing a street address to search, or click the map to place the pin."
								: undefined
						}
						isInvalid={Boolean(errors.street1)}
						label="Street"
						name="street1"
						onBlur={field.onBlur}
						onChange={field.onChange}
						placeholder="E.g. 123 Main St"
						ref={field.ref}
						value={field.value}
					/>
				)}
			/>
			<Controller
				control={control}
				name="street2"
				render={({ field }) => (
					<Input
						autoComplete="address-line2"
						label="Street 2"
						name="street2"
						onBlur={field.onBlur}
						onChange={field.onChange}
						placeholder="E.g. Suite 302"
						ref={field.ref}
						value={field.value}
					/>
				)}
			/>
			<Controller
				control={control}
				name="city"
				render={({ field }) => (
					<Input
						autoComplete="address-level2"
						errorMessage={errors.city?.message}
						isInvalid={Boolean(errors.city)}
						label="City"
						name="city"
						onBlur={field.onBlur}
						onChange={field.onChange}
						placeholder="E.g. Denver"
						ref={field.ref}
						value={field.value}
					/>
				)}
			/>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<Controller
					control={control}
					name="stateProvince"
					render={({ field }) => (
						<Input
							autoComplete="address-level1"
							errorMessage={errors.stateProvince?.message}
							isInvalid={Boolean(errors.stateProvince)}
							label="State / Province"
							name="stateProvince"
							onBlur={field.onBlur}
							onChange={field.onChange}
							placeholder="E.g. CO"
							ref={field.ref}
							value={field.value}
						/>
					)}
				/>
				<Controller
					control={control}
					name="postalCode"
					render={({ field }) => (
						<Input
							autoComplete="postal-code"
							errorMessage={errors.postalCode?.message}
							isInvalid={Boolean(errors.postalCode)}
							label="Postal code"
							name="postalCode"
							onBlur={field.onBlur}
							onChange={field.onChange}
							placeholder="E.g. 80202"
							ref={field.ref}
							value={field.value}
						/>
					)}
				/>
			</div>
		</div>
	);

	return (
		<>
			<Controller
				control={control}
				name="name"
				render={({ field }) => (
					<Input
						errorMessage={errors.name?.message}
						isInvalid={Boolean(errors.name)}
						isRequired
						label="Name"
						onBlur={field.onBlur}
						onChange={field.onChange}
						placeholder="Site name"
						ref={field.ref}
						value={field.value}
					/>
				)}
				rules={{ required: "Name is required" }}
			/>
			<Controller
				control={control}
				name="timezone"
				render={({ field }) => (
					<Input
						label="Timezone"
						onBlur={field.onBlur}
						onChange={field.onChange}
						placeholder="America/Denver"
						ref={field.ref}
						value={field.value}
					/>
				)}
			/>
			{wrapAddressFields(addressFields)}
			<hr className="my-1 border-secondary" />
			<div className="flex flex-col gap-2">
				<h3 className="text-sm font-semibold text-primary">Map Coordinates</h3>
				<p className="text-sm text-tertiary">
					Optional. Click the map or drag the marker to place the location pin
					and autofill the address, or enter latitude and longitude manually.
				</p>
			</div>
			<div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
				<Controller
					control={control}
					name="latitude"
					render={({ field }) => {
						const latitudeError =
							errors.latitude?.message ?? getLatitudeError(field.value);
						return (
							<Input
								className="w-full"
								errorMessage={latitudeError}
								inputMode="decimal"
								isInvalid={Boolean(latitudeError)}
								label="Latitude"
								onBlur={field.onBlur}
								onChange={field.onChange}
								placeholder="E.g. 39.7392"
								ref={field.ref}
								type="number"
								value={field.value}
							/>
						);
					}}
					rules={{
						validate: (value) => getLatitudeError(value) ?? true,
					}}
				/>
				<Controller
					control={control}
					name="longitude"
					render={({ field }) => {
						const longitudeError =
							errors.longitude?.message ?? getLongitudeError(field.value);
						return (
							<Input
								className="w-full"
								errorMessage={longitudeError}
								inputMode="decimal"
								isInvalid={Boolean(longitudeError)}
								label="Longitude"
								onBlur={field.onBlur}
								onChange={field.onChange}
								placeholder="E.g. -104.9903"
								ref={field.ref}
								type="number"
								value={field.value}
							/>
						);
					}}
					rules={{
						validate: (value) => getLongitudeError(value) ?? true,
					}}
				/>
			</div>
		</>
	);
};
